import { ButtonBase, Popover } from "@mui/material";
import type { JSX, ReactNode } from "react";
import { useState } from "react";

import { UserProfileCard } from "./UserProfileCard.js";

/**
 * Wraps a person's avatar/name as a keyboard-accessible anchor that opens the
 * profile-card popover on click. Drop it around any identity element at an
 * *informational* site (a message sender, a DM header, an admin row) — the
 * card handles its own data + actions.
 *
 * For a NON-real user id (`<= 0`: the "XOP Updates" bot / system posts,
 * pending-invite rows) it renders `children` inertly — no button, no popover —
 * so those never become clickable. `stopPropagation` on both the trigger and
 * the popover keeps it safe to nest inside a clickable row later.
 */
export function ProfileCardTrigger({
  userId,
  name,
  children,
}: {
  userId: number;
  name: string;
  children: ReactNode;
}): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const realUser = Number.isInteger(userId) && userId > 0;
  if (!realUser) return <>{children}</>;

  const close = (): void => setAnchorEl(null);

  return (
    <>
      <ButtonBase
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        aria-label={`View ${name}'s profile`}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          borderRadius: 1,
          font: "inherit",
          color: "inherit",
          textAlign: "inherit",
          cursor: "pointer",
          // Affordance for the text case (a clickable name); no visible effect
          // when the child is an avatar.
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {children}
      </ButtonBase>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: 3 } }}
      >
        <UserProfileCard userId={userId} name={name} onClose={close} />
      </Popover>
    </>
  );
}
